// @tennis-matches/ui-components/app-toast

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { APP_TOAST_SHOW_EVENT } from "./appToast.events.js";

function createToastId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * @param {(key: string, params?: Record<string, unknown>) => string} t
 */
export function AppToastHost({ t }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((row) => row.id !== id));
  }, []);

  useEffect(() => {
    const onShow = (event) => {
      const detail = event?.detail && typeof event.detail === "object" ? event.detail : {};
      const messageKey = String(detail.messageKey || "").trim();
      const rawMessage = String(detail.message || "").trim();
      const params = detail.params && typeof detail.params === "object" ? detail.params : undefined;
      const text = messageKey && typeof t === "function" ? t(messageKey, params) : rawMessage;
      if (!text) return;

      const id = createToastId();
      const kind = detail.kind === "error" || detail.kind === "info" ? detail.kind : "success";
      const durationMs = Number.isFinite(Number(detail.durationMs)) ? Number(detail.durationMs) : 3600;

      setToasts((prev) => [...prev.slice(-2), { id, kind, text }]);

      const timer = window.setTimeout(() => dismissToast(id), durationMs);
      timersRef.current.set(id, timer);
    };

    window.addEventListener(APP_TOAST_SHOW_EVENT, onShow);
    return () => {
      window.removeEventListener(APP_TOAST_SHOW_EVENT, onShow);
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [dismissToast, t]);

  if (!toasts.length || typeof document === "undefined") return null;

  return createPortal(
    <div className="app-toast-host" aria-live="polite" aria-relevant="additions">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`app-toast app-toast--${toast.kind}`}
          role="status"
        >
          <span className="app-toast__text">{toast.text}</span>
        </div>
      ))}
    </div>,
    document.body
  );
}
